import { useEffect, useState } from "react";
import { Icon } from "../components/Icon";
import { useI18n } from "../i18n";
import { isIos, isStandalone } from "../push/client";
import { onUpdateReady, pendingUpdate } from "./register";

const IOS_HINT_KEY = "fts.iosInstallHint.dismissed";

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine !== false));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(IOS_HINT_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * App-wide PWA notices (ADR 0019), on every page incl. the public checklist form:
 *   - "No connection" while the browser is offline,
 *   - "New version available — Reload" when an update is waiting (the user decides when),
 *   - on iPhone/iPad Safari, a dismissible "Add to Home Screen" hint (needed for notifications there).
 * Also points the manifest link to the current language (TR/EN app name).
 */
export function PwaStatus() {
  const { t, lang } = useI18n();
  const online = useOnline();
  const [update, setUpdate] = useState<(() => void) | null>(() => pendingUpdate());
  const [iosHint, setIosHint] = useState(() => typeof navigator !== "undefined" && isIos(navigator) && !isStandalone(navigator, window) && !readDismissed());

  useEffect(() => onUpdateReady(() => setUpdate(() => pendingUpdate())), []);

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (link) link.href = `/manifest.${lang}.webmanifest`;
  }, [lang]);

  const dismissIos = () => {
    setIosHint(false);
    try {
      localStorage.setItem(IOS_HINT_KEY, "1");
    } catch {
      // The hint simply shows again next time.
    }
  };

  if (online && !update && !iosHint) return null;
  return (
    <div className="pwa-notices">
      {!online && (
        <div className="pwa-notice pwa-offline" role="status">
          <Icon name="alert" size={16} />
          <span>{t("pwa.offline")}</span>
        </div>
      )}
      {update && (
        <div className="pwa-notice pwa-update" role="status">
          <span>{t("pwa.updateReady")}</span>
          <button type="button" className="button button-primary button-sm" onClick={() => update()}>
            {t("pwa.reload")}
          </button>
        </div>
      )}
      {iosHint && online && (
        <div className="pwa-notice pwa-ios" role="note">
          <span>{t("pwa.iosHint")}</span>
          <button type="button" className="icon-button" onClick={dismissIos} title={t("pwa.dismiss")} aria-label={t("pwa.dismiss")}>
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
